import { useTranslation } from 'react-i18next';
import { href } from 'react-router';

import {
  CalendarDaysIcon,
  ChartColumnIncreasingIcon,
  CircleQuestionMarkIcon,
  CrossIcon,
  FlaskConicalIcon,
  GraduationCapIcon,
  MailIcon,
  MegaphoneIcon,
  MessagesSquareIcon,
  PackageIcon,
  SettingsIcon,
  ShoppingCartIcon,
  StoreIcon,
  UserLockIcon,
  UsersIcon,
} from '@valwin/facto/icons';
import type {
  NavNode as FactoNavNode,
  NavTopLevel as FactoNavTopLevel,
} from '@valwin/facto/widgets';

import { useSession } from '~entities/session';
import { useGetSummary } from '~features/summary';
import type { NavId } from '~shared/navigation';
import { ROUTES, usePharmacyId } from '~shared/routes';

type NavNode = FactoNavNode<NavId>;
type NavTopLevel = FactoNavTopLevel<NavId>;

export function useNavigationItems(): NavTopLevel[] {
  const pharmacyId = usePharmacyId();
  const { t } = useTranslation();
  const { session } = useSession(pharmacyId);
  const { summary } = useGetSummary(pharmacyId, { enabled: !!session?.user });

  const routes = session?.routes;
  const pharmacy = session?.pharmacy;

  if (!routes || !pharmacy) {
    return [];
  }

  const items: NavTopLevel[] = [];

  const hasOnlineSales =
    routes.orders ||
    routes.catalog ||
    routes.promotions ||
    routes.cncConfiguration;

  if (routes.dashboard) {
    items.push({
      kind: 'link',
      id: 'dashboard',
      label: t('navigation dashboard'),
      icon: ChartColumnIncreasingIcon,
      to: href(ROUTES.dashboard, { pharmacyId }),
    });
  }

  // Section: Ma pharmacie
  const myPharmacyChildren: NavNode[] = [];

  const mySiteChildren: NavNode[] = [];

  if (routes.myPharmacy) {
    mySiteChildren.push({
      kind: 'link',
      id: 'my-pharmacy-infos',
      label: t('navigation generalInfos'),
      to: href(ROUTES.myPharmacy, { pharmacyId }),
    });
  }

  if (routes.contentV2) {
    mySiteChildren.push(
      {
        kind: 'link',
        id: 'sections',
        label: t('navigation sectionManagement'),
        to: href(ROUTES.sections, { pharmacyId }),
      },
      {
        kind: 'link',
        id: 'images',
        label: t('navigation imageManagement'),
        to: href(ROUTES.images, { pharmacyId }),
      },
      {
        kind: 'link',
        id: 'team',
        label: t('navigation team'),
        to: href(ROUTES.team, { pharmacyId }),
      },
      {
        kind: 'link',
        id: 'linings',
        label: t('navigation ranges'),
        to: href(ROUTES.linings, { pharmacyId }),
      },
      {
        kind: 'link',
        id: 'services',
        label: t('navigation services'),
        to: href(ROUTES.services, { pharmacyId }),
      },
      {
        kind: 'link',
        id: 'specialities',
        label: t('navigation specialties'),
        to: href(ROUTES.specialities, { pharmacyId }),
      }
    );
  }

  if (routes.videos) {
    mySiteChildren.push({
      kind: 'link',
      id: 'videos',
      label: t('navigation videoManagement'),
      to: href(ROUTES.videos, { pharmacyId }),
    });
  }

  if (routes.contentV2) {
    mySiteChildren.push({
      kind: 'link',
      id: 'tips',
      label: t('navigation usefulInfos'),
      to: href(ROUTES.tips, { pharmacyId }),
    });
  }

  if (mySiteChildren.length > 0) {
    myPharmacyChildren.push({
      kind: 'group',
      id: 'my-site',
      label: t('navigation myPharmacy'),
      icon: StoreIcon,
      children: mySiteChildren,
    });
  }

  const communicationChildren: NavNode[] = [];

  if (routes.happenings) {
    communicationChildren.push({
      kind: 'link',
      id: 'events',
      label: t('navigation animation'),
      to: href(ROUTES.content.events, { pharmacyId }),
    });
  }

  if (routes.communication) {
    communicationChildren.push({
      kind: 'link',
      id: 'communication',
      label: t('navigation suggestedContent'),
      to: href(ROUTES.communication, { pharmacyId }),
    });
  }

  if (routes.pharmacistWord) {
    communicationChildren.push({
      kind: 'link',
      id: 'pharmacist-word',
      label: t('navigation pharmacistWord'),
      to: href(ROUTES.content.pharmacistWord, { pharmacyId }),
    });
  }

  if (routes.blog) {
    communicationChildren.push({
      kind: 'link',
      id: 'blog',
      label: t('navigation articles'),
      to: href(ROUTES.content.blog, { pharmacyId }),
    });
  }

  if (routes.onDuty) {
    communicationChildren.push({
      kind: 'link',
      id: 'on-duty',
      label: t('navigation onDutyPharmacies'),
      to: href(ROUTES.onDuty, { pharmacyId }),
    });
  }

  if (routes.communicationSupport) {
    communicationChildren.push({
      kind: 'link',
      id: 'communication-media',
      label: t('navigation communicationSupports'),
      to: href(ROUTES.content.communicationMedia, { pharmacyId }),
    });
  }

  if (routes.promotionsSnc) {
    communicationChildren.push({
      kind: 'link',
      id: 'promotions-snc',
      label: t('navigation promotions'),
      to: href(ROUTES.promotions.promotionsSnC, { pharmacyId }),
    });
  }

  if (communicationChildren.length > 0) {
    myPharmacyChildren.push({
      kind: 'group',
      id: 'communication-group',
      label: t('navigation communication'),
      icon: MegaphoneIcon,
      children: communicationChildren,
    });
  }

  if (myPharmacyChildren.length > 0) {
    items.push({
      kind: 'section',
      id: 'section-my-pharmacy',
      label: t('navigation myPharmacy'),
      children: myPharmacyChildren,
    });
  }

  // Section: Vente en ligne
  const onlineSalesChildren: NavNode[] = [];

  if (routes.orders) {
    onlineSalesChildren.push({
      kind: 'link',
      id: 'orders',
      label: t('navigation baskets'),
      icon: ShoppingCartIcon,
      badge: summary?.newOrders || undefined,
      to: href(ROUTES.orders.root, { pharmacyId }),
    });
  }

  const catalogChildren: NavNode[] = [];

  if (routes.catalog) {
    catalogChildren.push({
      kind: 'link',
      id: 'catalog',
      label: t('navigation products'),
      to: href(ROUTES.catalog.root, { pharmacyId }),
    });
  }

  if (routes.promotions) {
    catalogChildren.push({
      kind: 'link',
      id: 'promotions-cnc',
      label: t('navigation promotions'),
      to: href(ROUTES.promotions.promotionsCnc, { pharmacyId }),
    });
  }

  if (catalogChildren.length > 0) {
    onlineSalesChildren.push({
      kind: 'group',
      id: 'catalog-group',
      label: t('navigation catalog'),
      icon: PackageIcon,
      children: catalogChildren,
    });
  }

  const configurationsChildren: NavNode[] = [];

  if (routes.cncConfiguration) {
    configurationsChildren.push({
      kind: 'link',
      id: 'cnc-configuration',
      label: t('navigation configuration'),
      to: href(ROUTES.cncConfiguration, { pharmacyId }),
    });
  }

  if (routes.generateQRCode && hasOnlineSales) {
    configurationsChildren.push({
      kind: 'link',
      id: 'qr-code',
      label: t('navigation qrCode'),
      to: href(ROUTES.catalog.generateQRCode, { pharmacyId }),
    });
  }

  if (configurationsChildren.length > 0) {
    onlineSalesChildren.push({
      kind: 'group',
      id: 'configurations-group',
      label: t('navigation configurations'),
      icon: SettingsIcon,
      children: configurationsChildren,
    });
  }

  if (onlineSalesChildren.length > 0) {
    items.push({
      kind: 'section',
      id: 'section-online-sales',
      label: t('navigation onlineSales'),
      children: onlineSalesChildren,
    });
  }

  // Section: Gestion patients
  const patientChildren: NavNode[] = [];

  if (routes.messaging) {
    patientChildren.push({
      kind: 'link',
      id: 'messaging',
      label: t('navigation messaging'),
      icon: MailIcon,
      badge: summary?.unreadMessages || undefined,
      to: href(ROUTES.messaging, { pharmacyId }),
    });
  }

  const meetingsChildren: NavNode[] = [];

  if (routes.meetings) {
    meetingsChildren.push({
      kind: 'link',
      id: 'meetings',
      label: t('navigation calendar'),
      to: href(ROUTES.meetings.root, { pharmacyId }),
    });
  }

  if (routes.waitingLists) {
    meetingsChildren.push({
      kind: 'link',
      id: 'waiting-list',
      label: t('navigation waitingLists'),
      to: href(ROUTES.waitingList.root, { pharmacyId }),
    });
  }

  if (routes.meetingSync) {
    meetingsChildren.push({
      kind: 'link',
      id: 'synchronization',
      label: t('navigation synchronization'),
      to: href(ROUTES.synchronization, { pharmacyId }),
    });
  }

  if (meetingsChildren.length > 0) {
    patientChildren.push({
      kind: 'group',
      id: 'meetings-group',
      label: t('navigation appointmentManagement'),
      icon: CalendarDaysIcon,
      children: meetingsChildren,
    });
  }

  if (routes.patient) {
    patientChildren.push({
      kind: 'link',
      id: 'patient-search',
      label: t('navigation patients'),
      icon: UsersIcon,
      to: href(ROUTES.patient.search, { pharmacyId }),
    });
  }

  if (routes.forms && pharmacy.groupId !== 'pharmabest-es') {
    patientChildren.push({
      kind: 'link',
      id: 'forms',
      label: t('navigation newMissions'),
      icon: CrossIcon,
      to: href(ROUTES.forms.root, { pharmacyId }),
    });
  }

  if (patientChildren.length > 0) {
    items.push({
      kind: 'section',
      id: 'section-patient-management',
      label: t('navigation patientManagement'),
      children: patientChildren,
    });
  }

  // Section: Relation groupement
  const groupRelationChildren: NavNode[] = [];

  if (routes.forum) {
    groupRelationChildren.push({
      kind: 'link',
      id: 'forum',
      label: t('navigation intranet'),
      icon: MessagesSquareIcon,
      badge: summary?.unreadForumMessages || undefined,
      to: href(ROUTES.forum, { pharmacyId }),
    });
  }

  if (routes.campaigns) {
    groupRelationChildren.push({
      kind: 'link',
      id: 'campaigns',
      label: t('navigation campaigns'),
      icon: FlaskConicalIcon,
      to: href(ROUTES.campaigns, { pharmacyId }),
    });
  }

  if (groupRelationChildren.length > 0) {
    items.push({
      kind: 'section',
      id: 'section-group-relation',
      label: t('navigation groupRelation'),
      children: groupRelationChildren,
    });
  }

  // Section: Recrutements
  const recruitmentChildren: NavNode[] = [];

  if (routes.jobs) {
    recruitmentChildren.push({
      kind: 'link',
      id: 'jobs',
      label: t('navigation jobOffers'),
      icon: GraduationCapIcon,
      to: href(ROUTES.jobs.root, { pharmacyId }),
    });
  }

  if (recruitmentChildren.length > 0) {
    items.push({
      kind: 'section',
      id: 'section-recruitment',
      label: t('navigation recruitment'),
      children: recruitmentChildren,
    });
  }

  // Section: Mon backoffice
  const backofficeChildren: NavNode[] = [];

  if (routes.accessConfiguration) {
    backofficeChildren.push({
      kind: 'link',
      id: 'access-configuration',
      label: t('navigation accessManagement'),
      icon: UserLockIcon,
      to: href(ROUTES.accessConfiguration, { pharmacyId }),
    });
  }

  if (routes.notices && !pharmacy.isPharmabest) {
    backofficeChildren.push({
      kind: 'link',
      id: 'notices',
      label: t('navigation needHelp'),
      icon: CircleQuestionMarkIcon,
      to: href(ROUTES.notices, { pharmacyId }),
    });
  }

  if (backofficeChildren.length > 0) {
    items.push({
      kind: 'section',
      id: 'section-backoffice',
      label: t('navigation myBackoffice'),
      children: backofficeChildren,
    });
  }

  return items;
}
